/* eslint-disable react/prop-types */
/**
 * Brokers admin page — CRUD for the `brokers` table (external land-brokerage
 * firms). Backed by `/api/broker_deals/brokers/...` (see brokerService.js).
 *
 * Hand-coded column defs + form fields (no metadata-driven panel) because
 * the `brokers` table isn't in the curated `/metadata?table_name=brokers`
 * seed yet. When that pattern hardens we can swap to WigMetadataCrudPanel.
 *
 * Delete flow:
 *   1. Popconfirm guard.
 *   2. If the backend returns 409 (deals still reference this broker), we
 *      pivot to an AntD Modal.confirm that offers a soft-deactivate (PUT
 *      with `status: 'INACTIVE'`) instead. The backend detail message
 *      includes the deal count.
 *
 * Vendor_code uniqueness is enforced server-side — 409 surfaces as an
 * inline form error on the Edit/Add modal.
 *
 * Agents child panel — two offboard paths:
 *   - Suspend (PUT `active: false`): reversible. The mapping row, its
 *     brokerage, display name and audit trail all stay; the server scopes the
 *     agent to nothing (zero deals, writes 403) and the deal Agent dropdown
 *     drops them. Reactivate flips it back. Prefer this.
 *   - Remove (DELETE): permanent — discards the name and audit trail.
 *
 * Agent SCOPE (the "Sees" column and field) — de-hub migration 088. Every
 * external user holds the same role (BD.BrokerAgent); the tier on their
 * roster row decides how much of their brokerage's book they see: all of it,
 * the deals in their assigned AOIs, or only the deals they are named on. See
 * utils/brokerTiers.js. This screen is where that is set, because it is
 * tenancy configuration — a brokerage must never be able to widen its own
 * scope.
 */
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from "@azure/msal-react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Empty,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Tag,
  Typography,
  message,
  notification,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  StopOutlined,
  UndoOutlined,
  UsergroupAddOutlined,
} from "@ant-design/icons";
import useMeasure from "react-use-measure";
import { useNavigate } from "react-router";

import {
  WigAntLayout,
  WigAntTable,
} from "@warwick-investment-group/wig-react-library";

import SignIn from "../signIn";
import { AppContext } from "../../AppContextProvider";
import usePermissions from "../../hooks/usePermissions";
import {
  BROKER_TIER_ADMIN,
  BROKER_TIER_LEAD,
  BROKER_TIER_OPTIONS,
  brokerTierLabel,
} from "../../utils/brokerTiers";
import { formatPhone, phoneDigits } from "../../utils/phone";
import brokerService from "../../services/brokerService";
import brokerAccessService from "../../services/brokerAccessService";
import dealsService from "../../services/dealsService";
import { apiErrorText } from "../../utils/apiError";

const { Title, Text } = Typography;
const { TextArea } = Input;

// Kept as a local alias so the existing call sites read unchanged; the
// formatting itself lives in utils/apiError so every screen phrases a 422 the
// same way.
const errText = (err) => apiErrorText(err);

const fmtDate = (v) => {
  if (!v) return "";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString();
};

const STATUS_OPTIONS = [
  { value: "ACTIVE", label: "ACTIVE" },
  { value: "INACTIVE", label: "INACTIVE" },
];

// Empty form template — used as the initial state for "Add" and as a base
// the Edit flow merges over.
const EMPTY_BROKER = {
  company_name: "",
  vendor_code: "",
  contact_name: "",
  contact_email: "",
  contact_phone: "",
  status: "ACTIVE",
  notes: "",
};

/**
 * BrokerFormModal — single Add/Edit modal. The parent owns the form values
 * and visibility; this component is mostly layout + validation.
 */
const BrokerFormModal = ({
  open,
  mode, // 'add' | 'edit'
  initialValues,
  saving,
  onCancel,
  onSubmit,
}) => {
  const [form] = Form.useForm();

  // Re-seed the form whenever the modal opens or the row under edit
  // changes. Form.useForm preserves prior state across renders; without
  // an explicit setFieldsValue() the next open would still show the old
  // row's values.
  useEffect(() => {
    if (open) {
      form.resetFields();
      form.setFieldsValue(initialValues || EMPTY_BROKER);
    }
  }, [open, initialValues, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      await onSubmit(values, { setFieldError: form.setFields });
    } catch {
      // validation errors / onSubmit re-throws — surfaced inline by AntD
      // or by the parent's notification.error.
    }
  };

  return (
    <Modal
      open={open}
      title={mode === "add" ? "Add broker" : "Edit broker"}
      onCancel={onCancel}
      onOk={handleOk}
      confirmLoading={saving}
      okText={mode === "add" ? "Create" : "Save"}
      destroyOnHidden
      width={600}
    >
      <Form form={form} layout="vertical" initialValues={EMPTY_BROKER}>
        <Form.Item
          label="Company name"
          name="company_name"
          rules={[{ required: true, message: "Company name is required" }]}
        >
          <Input autoFocus placeholder="e.g. RedSky Land, LLC" />
        </Form.Item>

        <Form.Item label="Vendor code" name="vendor_code">
          <Input placeholder="From accounting system" />
        </Form.Item>

        <Form.Item label="Contact name" name="contact_name">
          <Input />
        </Form.Item>

        <Form.Item
          label="Contact email"
          name="contact_email"
          rules={[{ type: "email", message: "Enter a valid email address" }]}
        >
          <Input type="email" />
        </Form.Item>

        {/* Masked phone: `normalize` stores digits only ("1234567890");
            `getValueProps` formats the stored digits back to "(123) 456-7890"
            for display/entry. Works for legacy already-formatted values too. */}
        <Form.Item
          label="Contact phone"
          name="contact_phone"
          normalize={phoneDigits}
          getValueProps={(v) => ({ value: formatPhone(v) })}
        >
          <Input inputMode="tel" placeholder="(555) 123-4567" maxLength={14} />
        </Form.Item>

        <Form.Item
          label="Status"
          name="status"
          rules={[{ required: true, message: "Status is required" }]}
        >
          <Select options={STATUS_OPTIONS} />
        </Form.Item>

        <Form.Item label="Notes" name="notes">
          <TextArea rows={3} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

/**
 * AddAgentModal — add or edit a broker-agent for the currently selected broker.
 *
 * Two modes:
 *   - mode="add": capture Name (required) + Email (required) and grant access.
 *     On a 409 (the email is already mapped to a *different* broker) the modal
 *     pivots to an inline "Reassign to this broker" affordance that calls the
 *     PUT/reassign path instead of failing (carrying the entered name along).
 *   - mode="edit": Email is fixed (natural key, read-only); the admin edits the
 *     agent's display Name and saves via the PUT/update path.
 *
 * The agent's Name is the value the deal `agent` dropdown offers, scoped to the
 * agent's brokerage.
 *
 * The parent owns auth + the service calls; this modal only owns the form and
 * the create-vs-reassign UX. `onSubmit`/`onReassign`/`onUpdate` return the
 * axios promise so the modal can inspect the error status.
 *
 * FUTURE: add mode is email-entry only for now. A "pick from Entra directory"
 * mode (Entra B2B guest lookup) can slot in alongside the email input. Not
 * built yet.
 */
const AddAgentModal = ({
  open,
  mode = "add",
  broker,
  initialAgent,
  aoiOptions = [],
  onCancel,
  onSubmit,
  onReassign,
  onUpdate,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  // When set ({ email, name }), the entered email is already mapped elsewhere
  // (409) and we surface the reassign affordance instead of the plain error.
  const [conflict, setConflict] = useState(null);

  const isEdit = mode === "edit";

  useEffect(() => {
    if (open) {
      form.resetFields();
      form.setFieldsValue({
        name: initialAgent?.name || "",
        email: initialAgent?.email || "",
        // A row saved before the tiers existed has no `tier`; it means what
        // it always meant — the whole brokerage.
        tier: initialAgent?.tier || BROKER_TIER_ADMIN,
        aoi_ids: initialAgent?.aoi_ids || [],
      });
      setConflict(null);
      setSaving(false);
    }
  }, [open, form, initialAgent]);

  // Watched so the AOI picker appears only for the tier that scopes on it.
  const tier = Form.useWatch("tier", form) || BROKER_TIER_ADMIN;

  const handleAdd = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return; // AntD surfaces validation errors inline
    }
    setSaving(true);
    setConflict(null);
    try {
      // AOI assignments are only meaningful for the lead tier, but they are
      // sent for every tier so switching someone to Area lead later doesn't
      // mean re-picking their areas. The server stores them dormant.
      const scope = { tier: values.tier, aoi_ids: values.aoi_ids || [] };
      if (isEdit) {
        await onUpdate({ email: values.email, name: values.name, ...scope });
        message.success("Agent updated");
      } else {
        await onSubmit({ email: values.email, name: values.name, ...scope });
        message.success("Agent access granted");
      }
      setSaving(false);
      onSuccess();
    } catch (err) {
      setSaving(false);
      const status = err?.response?.status;
      if (!isEdit && status === 409) {
        // Email already mapped to another broker → offer reassignment,
        // carrying the entered name AND scope so the reassign doesn't lose
        // them.
        setConflict({
          email: values.email,
          name: values.name,
          tier: values.tier,
          aoi_ids: values.aoi_ids || [],
        });
        return;
      }
      if (status === 400) {
        // Empty email / bad brokerage FK — pin it on the email field.
        form.setFields([{ name: "email", errors: [errText(err)] }]);
        return;
      }
      notification.error({
        message: isEdit
          ? "Failed to update agent"
          : "Failed to grant agent access",
        description: errText(err),
      });
    }
  };

  const handleReassign = async () => {
    if (!conflict) return;
    setSaving(true);
    try {
      await onReassign({
        email: conflict.email,
        name: conflict.name,
        tier: conflict.tier,
        aoi_ids: conflict.aoi_ids || [],
      });
      message.success("Agent reassigned to this broker");
      setSaving(false);
      onSuccess();
    } catch (err) {
      setSaving(false);
      notification.error({
        message: "Failed to reassign agent",
        description: errText(err),
      });
    }
  };

  const brokerLabel = broker?.company_name ? ` — ${broker.company_name}` : "";

  return (
    <Modal
      open={open}
      title={`${isEdit ? "Edit agent" : "Add agent"}${brokerLabel}`}
      onCancel={onCancel}
      onOk={handleAdd}
      okText={isEdit ? "Save" : "Add agent"}
      // Hide the default OK while resolving a conflict — the reassign/cancel
      // buttons in the Alert drive that flow instead.
      okButtonProps={conflict ? { style: { display: "none" } } : undefined}
      confirmLoading={saving}
      destroyOnHidden
      width={520}
    >
      <Form form={form} layout="vertical" disabled={saving}>
        <Form.Item
          label="Agent name"
          name="name"
          rules={[{ required: true, message: "Name is required" }]}
          tooltip="Display name shown in the deal Agent dropdown"
        >
          <Input autoFocus placeholder="e.g. Jane Smith" />
        </Form.Item>

        <Form.Item
          label="Agent email"
          name="email"
          rules={[
            { required: true, message: "Email is required" },
            { type: "email", message: "Enter a valid email address" },
          ]}
          tooltip="The broker agent's login email (must match their Azure AD account)"
        >
          <Input
            disabled={isEdit || !!conflict}
            placeholder="e.g. agent@firm.com"
          />
        </Form.Item>

        <Form.Item
          label="Sees"
          name="tier"
          rules={[{ required: true, message: "Pick what this user can see" }]}
          tooltip={
            "How much of the brokerage's book this user sees. Everything " +
            "else about what a broker user may DO is the same across the " +
            "three — this only changes which deals they can reach."
          }
        >
          <Select options={BROKER_TIER_OPTIONS} />
        </Form.Item>

        {tier === BROKER_TIER_LEAD && (
          <Form.Item
            label="AOIs"
            name="aoi_ids"
            rules={[
              {
                required: true,
                // An area lead with no AOIs sees NOTHING (the server fails
                // closed rather than falling back to the brokerage), so this
                // is caught here where it is still one click to fix.
                message: "An area lead needs at least one AOI",
                type: "array",
                min: 1,
              },
            ]}
            tooltip="Only deals in these AOIs will be visible to this user"
          >
            <Select
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Pick one or more AOIs"
              options={aoiOptions}
            />
          </Form.Item>
        )}
      </Form>

      {conflict && (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 8 }}
          message="Email already mapped to another broker"
          description={
            <Space direction="vertical" size={8}>
              <span>
                <Text strong>{conflict.email}</Text> already has access to a
                different broker. Reassign them to{" "}
                <Text strong>{broker?.company_name || "this broker"}</Text>?
              </span>
              <Space>
                <Button
                  type="primary"
                  loading={saving}
                  onClick={handleReassign}
                >
                  Reassign to this broker
                </Button>
                <Button onClick={() => setConflict(null)}>Cancel</Button>
              </Space>
            </Space>
          }
        />
      )}
    </Modal>
  );
};

const Brokers = () => {
  const [ref, bounds] = useMeasure();
  const { token, getToken, isTokenExpired, user, theme } =
    useContext(AppContext);
  const { can } = usePermissions();
  const navigate = useNavigate();
  // Gates the screen itself. The brokerage roster is internal-only —
  // BD.BrokerAgent / BD.TitleAgent are kept out (nav tab hidden; this route
  // self-redirects as defense-in-depth). UI/UX gate only — /brokers is the
  // real boundary.
  const allowed = can("brokers.view");
  // Gates the child "Agents" (broker-access) panel. Users without access
  // management see only the Brokers master. UI/UX gate only — the
  // /broker_agent_access API is the real boundary (BD.Landman, which BD.Land
  // inherits; de / admin / BD.Admin bypass).
  const canManageAccess = can("brokers.manageAgents");
  // Gates the broker master's add/edit affordances. Row select + navigation
  // stay open to everyone; only mutation controls are gated. UI/UX gate only —
  // the /brokers API is the real boundary (BD.Admin + de/admin superusers).
  const canManageBrokers = can("admin.brokers");

  const [rows, setRows] = useState([]);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [includeInactive, setIncludeInactive] = useState(false);

  // Master/detail selection — the broker whose Agents child panel is shown.
  const [selectedBrokerId, setSelectedBrokerId] = useState(null);

  // Broker-access (agent) mappings. Fetched once (no server-side brokerage
  // filter) and filtered client-side to the selected broker.
  const [accessRows, setAccessRows] = useState([]);
  // AOI master — options for the Area lead tier's AOI assignment.
  const [aois, setAois] = useState([]);
  const [accessLoading, setAccessLoading] = useState(false);
  const [accessError, setAccessError] = useState(null);
  const [agentModalOpen, setAgentModalOpen] = useState(false);
  const [agentModalMode, setAgentModalMode] = useState("add"); // 'add' | 'edit'
  const [editingAgent, setEditingAgent] = useState(null);

  // Broker Add/Edit modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("add"); // 'add' | 'edit'
  const [editingRow, setEditingRow] = useState(null);
  const [saving, setSaving] = useState(false);

  const getAuth = useCallback(async () => {
    const userOid = user?.idTokenClaims?.oid || user?.localAccountId;
    if (!userOid) throw new Error("OID not found in user claims.");
    let tk = token;
    if (!tk || isTokenExpired(tk)) tk = await getToken();
    return { token: tk, userOid };
  }, [token, getToken, isTokenExpired, user]);

  // Group deals by brokerage_id once so the grid can show a "# deals" count
  // without an extra round-trip per row. Field name comes from
  // deals.brokerage_id (the FK to brokers.id).
  const dealsByBroker = useMemo(() => {
    const map = new Map();
    (deals || []).forEach((d) => {
      const bid = d?.brokerage_id;
      if (bid == null) return;
      map.set(bid, (map.get(bid) || 0) + 1);
    });
    return map;
  }, [deals]);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMsg(null);
      const { token: tk, userOid } = await getAuth();

      // Fire brokers + deals in parallel. The deals call is best-effort —
      // if it fails (e.g. permission), we still show the brokers grid; the
      // "# deals" column just renders 0s.
      const [brokersResp, dealsResp] = await Promise.allSettled([
        brokerService.listBrokers({
          token: tk,
          userOid,
          includeInactive,
        }),
        dealsService.listDeals({
          token: tk,
          userOid,
          filters: { limit: 1000 },
        }),
      ]);

      if (brokersResp.status === "fulfilled") {
        const data = brokersResp.value?.data?.data;
        const list = Array.isArray(data) ? data : [];
        setRows(list);
        // Auto-select the first broker on load; keep the current selection if
        // it still exists (e.g. after a refresh following an edit).
        setSelectedBrokerId((prev) =>
          prev != null && list.some((b) => b.id === prev)
            ? prev
            : list[0]?.id ?? null
        );
      } else {
        throw brokersResp.reason;
      }

      if (dealsResp.status === "fulfilled") {
        const data = dealsResp.value?.data?.data;
        setDeals(Array.isArray(data) ? data : []);
      } else {
        // non-fatal
        console.warn("brokers page: deals fetch failed", dealsResp.reason);
        setDeals([]);
      }
    } catch (err) {
      console.error("Error loading brokers:", err);
      setErrorMsg(err?.message || String(err));
    } finally {
      setLoading(false);
    }
  }, [getAuth, includeInactive]);

  // Redirect users who lack the permission (defense-in-depth for the route;
  // the API is the real boundary).
  useEffect(() => {
    if (!allowed) navigate("/", { replace: true });
  }, [allowed, navigate]);

  useEffect(() => {
    if (allowed) refresh();
  }, [allowed, refresh]);

  // Broker-access (agent) mappings — admin-only. The endpoint returns ALL
  // rows (no server-side brokerage filter), so we fetch once and filter
  // client-side by `brokerage_id` for the selected broker.
  const refreshAccess = useCallback(async () => {
    if (!canManageAccess) return;
    try {
      setAccessLoading(true);
      setAccessError(null);
      const { token: tk, userOid } = await getAuth();
      const resp = await brokerAccessService.listBrokerAccess({
        token: tk,
        userOid,
      });
      const data = resp?.data?.data;
      setAccessRows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error loading broker access:", err);
      setAccessError(errText(err));
    } finally {
      setAccessLoading(false);
    }
  }, [canManageAccess, getAuth]);

  useEffect(() => {
    refreshAccess();
  }, [refreshAccess]);

  // AOI options for the Area lead tier's assignment picker. Loaded once with
  // the roster rather than per modal-open (the axios cache is inert, so a
  // per-open fetch would be a real round-trip every time). Best-effort: the
  // Scope column falls back to raw ids if this fails, and the API is the
  // boundary either way.
  useEffect(() => {
    if (!canManageAccess) return;
    let cancelled = false;
    (async () => {
      try {
        const { token: tk, userOid } = await getAuth();
        const resp = await dealsService.listAois({ token: tk, userOid });
        const data = resp?.data?.data;
        if (!cancelled) setAois(Array.isArray(data) ? data : []);
      } catch (err) {
        console.warn("brokers page: AOI fetch failed", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canManageAccess, getAuth]);

  const aoiOptions = useMemo(
    () =>
      (aois || []).map((a) => ({ value: a.id, label: a.name || `#${a.id}` })),
    [aois]
  );
  const aoiNameById = useMemo(() => {
    const map = new Map();
    (aois || []).forEach((a) => map.set(a.id, a.name || `#${a.id}`));
    return map;
  }, [aois]);

  // ---------------------------------------------------------------------
  // Mutations
  // ---------------------------------------------------------------------

  // Fields the user can edit through this form. Anything else on the
  // editing row (id, created_at/by, updated_at/by) is server-managed
  // and would be rejected by the API's `extra="forbid"` Pydantic config
  // with HTTP 422. AntD seeds the form with the full row object on
  // edit, so we explicitly project down to these keys before posting.
  const EDITABLE_KEYS = Object.keys(EMPTY_BROKER);

  // The form requires these two, so a blank one is never a deliberate clear —
  // it can only be a validation gap. Everything else is nullable and the user
  // is allowed to empty it back out.
  const REQUIRED_KEYS = ["company_name", "status"];
  const NULLABLE_KEYS = EDITABLE_KEYS.filter((k) => !REQUIRED_KEYS.includes(k));

  // Blank strings are handled differently per mode, and the difference is the
  // point: the PUT is partial (`exclude_unset`), so a key missing from the body
  // means "leave this column alone". Dropping a cleared field there silently
  // discards the edit — the user blanks Contact email, saves, and the old
  // address comes right back. On edit we send an explicit `null` to clear. On
  // create there is nothing to clear, so blanks are simply omitted and the
  // column lands as NULL.
  const buildBody = (values, { mode = "add" } = {}) =>
    Object.fromEntries(
      Object.entries(values || {}).flatMap(([k, v]) => {
        if (!EDITABLE_KEYS.includes(k)) return [];
        if (typeof v === "string" && v.trim() === "") {
          return mode === "edit" && NULLABLE_KEYS.includes(k)
            ? [[k, null]]
            : [];
        }
        return [[k, v]];
      })
    );

  const handleCreate = async (values, helpers) => {
    setSaving(true);
    try {
      const { token: tk, userOid } = await getAuth();
      await brokerService.createBroker({
        token: tk,
        userOid,
        body: buildBody(values),
      });
      message.success("Broker created");
      setModalOpen(false);
      await refresh();
    } catch (err) {
      handleSaveError(err, helpers);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (values, helpers) => {
    setSaving(true);
    try {
      const { token: tk, userOid } = await getAuth();
      await brokerService.updateBroker({
        token: tk,
        userOid,
        brokerId: editingRow.id,
        body: buildBody(values, { mode: "edit" }),
      });
      message.success("Broker updated");
      setModalOpen(false);
      await refresh();
    } catch (err) {
      handleSaveError(err, helpers);
    } finally {
      setSaving(false);
    }
  };

  // Surface a save error either as an inline form field error (when we can
  // pin it on vendor_code) or as a top-level notification otherwise.
  const handleSaveError = (err, helpers) => {
    const status = err?.response?.status;
    const detail = apiErrorText(err, { fallback: "Save failed" });

    if (status === 409 && /vendor_code/i.test(String(detail))) {
      helpers?.setFieldError?.([
        { name: "vendor_code", errors: [String(detail)] },
      ]);
      return;
    }
    notification.error({
      message: "Broker save failed",
      description: String(detail),
    });
  };

  // Delete flow — 409 means there are still deals pointing at this row;
  // pivot the prompt to suggest the soft-deactivate fallback.
  const handleDelete = async (row) => {
    try {
      const { token: tk, userOid } = await getAuth();
      await brokerService.deleteBroker({
        token: tk,
        userOid,
        brokerId: row.id,
      });
      message.success("Broker deleted");
      await refresh();
    } catch (err) {
      const status = err?.response?.status;
      const detail = apiErrorText(err, { fallback: "" });
      if (status === 409) {
        Modal.confirm({
          title: "Can't delete this broker",
          content: `${detail || "This broker is referenced by existing deals."} Set status to INACTIVE instead?`,
          okText: "Set INACTIVE",
          cancelText: "Cancel",
          onOk: async () => {
            try {
              const { token: tk2, userOid: oid2 } = await getAuth();
              await brokerService.updateBroker({
                token: tk2,
                userOid: oid2,
                brokerId: row.id,
                body: { status: "INACTIVE" },
              });
              message.success("Broker deactivated");
              await refresh();
            } catch (innerErr) {
              notification.error({
                message: "Failed to deactivate broker",
                description: apiErrorText(innerErr),
              });
            }
          },
        });
      } else {
        notification.error({
          message: "Failed to delete broker",
          description: String(detail || err),
        });
      }
    }
  };

  // ---------------------------------------------------------------------
  // Agents (broker-access) — child panel mutations
  // ---------------------------------------------------------------------

  // The parent owns auth + the service call; AddAgentModal inspects the
  // returned promise's error status to drive its create-vs-reassign UX.
  const handleAddAgent = async ({ email, name, tier, aoi_ids }) => {
    const { token: tk, userOid } = await getAuth();
    return brokerAccessService.createBrokerAccess({
      token: tk,
      userOid,
      body: { email, brokerage_id: selectedBrokerId, name, tier, aoi_ids },
    });
  };

  const handleReassignAgent = async ({ email, name, tier, aoi_ids }) => {
    const { token: tk, userOid } = await getAuth();
    return brokerAccessService.updateBrokerAccess({
      token: tk,
      userOid,
      email,
      body: { brokerage_id: selectedBrokerId, name, tier, aoi_ids },
    });
  };

  // Edit an existing agent's display name / scope (email + brokerage stay
  // put). Reuses the PUT/update path; brokerage_id is re-sent so the row's
  // mapping is unchanged even if the backend treats a bare name update as a
  // full replace. `aoi_ids` REPLACES the stored assignment, which is what the
  // modal's picker shows, so an unchanged picker re-sends the same set.
  const handleUpdateAgent = async ({ email, name, tier, aoi_ids }) => {
    const { token: tk, userOid } = await getAuth();
    return brokerAccessService.updateBrokerAccess({
      token: tk,
      userOid,
      email,
      body: { brokerage_id: selectedBrokerId, name, tier, aoi_ids },
    });
  };

  const handleRemoveAgent = async (row) => {
    try {
      const { token: tk, userOid } = await getAuth();
      await brokerAccessService.deleteBrokerAccess({
        token: tk,
        userOid,
        email: row.email,
      });
      message.success("Agent access removed");
      await brokerAccessService.invalidateCache();
      await refreshAccess();
    } catch (err) {
      notification.error({
        message: "Failed to remove agent access",
        description: errText(err),
      });
    }
  };

  /**
   * Suspend (nextActive=false) or reactivate (nextActive=true) an agent.
   *
   * The soft counterpart to Remove: the mapping row — brokerage, display name,
   * granted-by/at audit — survives, but the server scopes a suspended agent to
   * nothing (they see zero deals, writes 403) and the deal `agent` dropdown
   * drops them, so no new deal can be assigned to them. Deals that already
   * carry the agent's name keep it (the deal column is free text).
   *
   * `brokerage_id` is re-sent from the row because the PUT body requires it;
   * `name` is re-sent so a backend that treats the update as a full replace
   * can't blank it out.
   */
  const handleToggleAgentActive = async (row, nextActive) => {
    try {
      const { token: tk, userOid } = await getAuth();
      await brokerAccessService.updateBrokerAccess({
        token: tk,
        userOid,
        email: row.email,
        body: {
          brokerage_id: row.brokerage_id,
          name: row.name,
          active: nextActive,
        },
      });
      message.success(nextActive ? "Agent reactivated" : "Agent suspended");
      await brokerAccessService.invalidateCache();
      await refreshAccess();
    } catch (err) {
      notification.error({
        message: nextActive
          ? "Failed to reactivate agent"
          : "Failed to suspend agent",
        description: errText(err),
      });
    }
  };

  const openAddAgent = () => {
    setAgentModalMode("add");
    setEditingAgent(null);
    setAgentModalOpen(true);
  };

  const openEditAgent = (row) => {
    setAgentModalMode("edit");
    setEditingAgent(row);
    setAgentModalOpen(true);
  };

  const onAgentModalSuccess = async () => {
    setAgentModalOpen(false);
    // GETs are cached for 5 minutes with no mutation-driven eviction, so drop
    // the cache before re-reading or the panel re-renders the pre-save list.
    await brokerAccessService.invalidateCache();
    await refreshAccess();
  };

  // Selected broker + its agents (client-side filter on brokerage_id).
  const selectedBroker = useMemo(
    () => rows.find((b) => b.id === selectedBrokerId) || null,
    [rows, selectedBrokerId]
  );
  const agentsForSelected = useMemo(
    () => (accessRows || []).filter((r) => r.brokerage_id === selectedBrokerId),
    [accessRows, selectedBrokerId]
  );

  // ---------------------------------------------------------------------
  // Grid wiring
  // ---------------------------------------------------------------------

  const openAdd = () => {
    setModalMode("add");
    setEditingRow(null);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setModalMode("edit");
    setEditingRow(row);
    setModalOpen(true);
  };

  const columns = useMemo(
    () => [
      {
        dataIndex: "company_name",
        title: "Company",
        key: "company_name",
        sorter: (a, b) =>
          String(a?.company_name ?? "").localeCompare(String(b?.company_name ?? "")),
      },
      {
        dataIndex: "vendor_code",
        title: "Vendor code",
        key: "vendor_code",
        width: 160,
        sorter: (a, b) =>
          String(a?.vendor_code ?? "").localeCompare(String(b?.vendor_code ?? "")),
      },
      {
        dataIndex: "contact_name",
        title: "Contact",
        key: "contact_name",
        width: 180,
      },
      {
        dataIndex: "contact_email",
        title: "Email",
        key: "contact_email",
      },
      {
        dataIndex: "contact_phone",
        title: "Phone",
        key: "contact_phone",
        width: 160,
        render: (v) => formatPhone(v) || "",
      },
      {
        dataIndex: "status",
        title: "Status",
        key: "status",
        width: 110,
        sorter: (a, b) =>
          String(a?.status ?? "").localeCompare(String(b?.status ?? "")),
        render: (value) => {
          if (!value) return null;
          const color = value === "ACTIVE" ? "green" : "default";
          return <Tag color={color}>{value}</Tag>;
        },
      },
      {
        title: "# deals",
        key: "deals_count",
        width: 110,
        align: "right",
        render: (_, row) =>
          row?.id != null ? dealsByBroker.get(row.id) || 0 : 0,
        sorter: (a, b) =>
          (dealsByBroker.get(a?.id) || 0) - (dealsByBroker.get(b?.id) || 0),
      },
      {
        title: "Actions",
        key: "actions",
        width: 130,
        fixed: "right",
        align: "center",
        // Stop row-click selection from firing when the Edit/Delete controls
        // are used (row onClick sets the selected broker for the child panel).
        onCell: () => ({ onClick: (e) => e.stopPropagation() }),
        render: (_, row) => {
          if (!row || !canManageBrokers) return null;
          return (
            <Space size="small" onClick={(e) => e.stopPropagation()}>
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => openEdit(row)}
                aria-label={`Edit ${row.company_name}`}
              />
              <Popconfirm
                title="Delete broker?"
                description={`Delete broker "${row.company_name}"? This cannot be undone.`}
                okText="Delete"
                okType="danger"
                cancelText="Cancel"
                onConfirm={() => handleDelete(row)}
              >
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  aria-label={`Delete ${row.company_name}`}
                />
              </Popconfirm>
            </Space>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dealsByBroker, canManageBrokers]
  );

  // Columns for the child Agents table. Name + email + Status (the `active`
  // suspend flag) + audit (granted by/at) + Edit / Suspend-Reactivate / Remove
  // actions.
  const agentColumns = useMemo(
    () => [
      {
        dataIndex: "name",
        title: "Name",
        key: "name",
        sorter: (a, b) =>
          String(a?.name ?? "").localeCompare(String(b?.name ?? "")),
      },
      {
        dataIndex: "email",
        title: "Email",
        key: "email",
        sorter: (a, b) =>
          String(a?.email ?? "").localeCompare(String(b?.email ?? "")),
      },
      {
        // Row-scope tier + the AOIs it scopes on. One column, because the
        // AOI list is meaningless without the tier and unreadable as a
        // separate one: a lead's areas are the whole point of the tier, and
        // an admin/agent row's stored AOIs are dormant, not in force.
        dataIndex: "tier",
        title: "Sees",
        key: "tier",
        width: 220,
        sorter: (a, b) =>
          String(brokerTierLabel(a?.tier)).localeCompare(
            String(brokerTierLabel(b?.tier))
          ),
        render: (value, row) => {
          const isLead = (value || BROKER_TIER_ADMIN) === BROKER_TIER_LEAD;
          const ids = row?.aoi_ids || [];
          return (
            <Space direction="vertical" size={0}>
              <Text>{brokerTierLabel(value)}</Text>
              {isLead && (
                <Text type={ids.length ? "secondary" : "danger"} style={{ fontSize: 12 }}>
                  {ids.length
                    ? ids.map((id) => aoiNameById.get(id) || `#${id}`).join(", ")
                    : "No AOIs — sees nothing"}
                </Text>
              )}
            </Space>
          );
        },
      },
      {
        // `active` is the suspend flag: false = access revoked (sees zero
        // deals) and hidden from the deal Agent dropdown, row preserved.
        // Treat a missing value as active — the column is NOT NULL DEFAULT
        // true server-side, so only a pre-migration payload can omit it.
        dataIndex: "active",
        title: "Status",
        key: "active",
        width: 120,
        sorter: (a, b) =>
          Number(a?.active !== false) - Number(b?.active !== false),
        render: (value) =>
          value === false ? (
            <Tag color="orange">SUSPENDED</Tag>
          ) : (
            <Tag color="green">ACTIVE</Tag>
          ),
      },
      {
        dataIndex: "created_by",
        title: "Granted by",
        key: "created_by",
        width: 220,
        sorter: (a, b) =>
          String(a?.created_by ?? "").localeCompare(
            String(b?.created_by ?? "")
          ),
      },
      {
        dataIndex: "created_at",
        title: "Granted",
        key: "created_at",
        width: 200,
        sorter: (a, b) =>
          String(a?.created_at ?? "").localeCompare(
            String(b?.created_at ?? "")
          ),
        render: (v) => fmtDate(v),
      },
      {
        title: "Actions",
        key: "actions",
        width: 170,
        align: "center",
        render: (_, row) => {
          if (!row) return null;
          const isSuspended = row.active === false;
          return (
            <Space size="small">
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => openEditAgent(row)}
                aria-label={`Edit ${row.email}`}
              />
              {/* Suspend / reactivate — the reversible offboard. Keeps the
                  mapping (and its audit trail) but revokes access. */}
              <Popconfirm
                title={isSuspended ? "Reactivate agent?" : "Suspend agent?"}
                description={
                  isSuspended
                    ? `Restore access for "${row.email}"? They will see this broker's deals again and reappear in the deal Agent dropdown.`
                    : `Suspend "${row.email}"? They will see zero deals and drop out of the deal Agent dropdown until reactivated. Deals already assigned to them keep the name.`
                }
                okText={isSuspended ? "Reactivate" : "Suspend"}
                okType={isSuspended ? "primary" : "danger"}
                cancelText="Cancel"
                onConfirm={() => handleToggleAgentActive(row, isSuspended)}
              >
                <Button
                  size="small"
                  icon={
                    isSuspended ? <UndoOutlined /> : <StopOutlined />
                  }
                  aria-label={`${isSuspended ? "Reactivate" : "Suspend"} ${
                    row.email
                  }`}
                />
              </Popconfirm>
              <Popconfirm
                title="Remove agent?"
                description={`Remove access for "${row.email}"? They will see zero deals until re-linked.`}
                okText="Remove"
                okType="danger"
                cancelText="Cancel"
                onConfirm={() => handleRemoveAgent(row)}
              >
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  aria-label={`Remove ${row.email}`}
                />
              </Popconfirm>
            </Space>
          );
        },
      },
    ],
    // The Sees column renders AOI names, so it has to rebuild when the AOI
    // master arrives (it loads after the roster).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [aoiNameById]
  );

  // Initial values handed to the modal — for Edit we hydrate from the row
  // selected in the grid, but project down to editable keys only (the
  // row also has server-managed columns id/created_*/updated_* that the
  // API rejects under `extra="forbid"`).
  const modalInitialValues = useMemo(() => {
    if (modalMode === "edit" && editingRow) {
      return Object.keys(EMPTY_BROKER).reduce((acc, k) => {
        acc[k] = editingRow[k] ?? EMPTY_BROKER[k];
        return acc;
      }, {});
    }
    return EMPTY_BROKER;
  }, [modalMode, editingRow]);

  // Leave room for the child Agents card below the master when it's shown.
  const gridHeight = canManageAccess
    ? Math.max(bounds.height - 460, 260)
    : Math.max(bounds.height - 220, 400);

  if (!allowed) return null;

  return (
    <React.Fragment>
      <AuthenticatedTemplate>
        <Row ref={ref}>
          <WigAntLayout
            darkMode={theme === "dark"}
            layoutStyle={{
              height: "93vh",
              width: "99vw",
              margin: 0,
              padding: 0,
              overflow: "hidden",
            }}
            contentStyle={{
              minHeight: "70vh",
              overflow: "auto",
              padding: 12,
              margin: 0,
            }}
            showFooter={false}
            showHeader={false}
            siderProps={{ collapsible: true, collapsed: true, width: 0 }}
            showDefaultMenu={false}
            content={
              <Space direction="vertical" size={12} style={{ width: "100%" }}>
                <Row justify="space-between" align="middle">
                  <Title level={3} style={{ margin: 0 }}>
                    Brokers
                  </Title>
                  <Space>
                    <Checkbox
                      checked={includeInactive}
                      onChange={(e) => setIncludeInactive(e.target.checked)}
                    >
                      Show inactive
                    </Checkbox>
                    {canManageBrokers && (
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={openAdd}
                      >
                        Add Broker
                      </Button>
                    )}
                  </Space>
                </Row>

                {errorMsg && (
                  <Alert
                    type="error"
                    showIcon
                    message="Failed to load brokers"
                    description={errorMsg}
                  />
                )}

                {!loading && rows.length === 0 && !errorMsg ? (
                  <Alert
                    type="info"
                    showIcon
                    message="No brokers yet"
                    description={
                      includeInactive
                        ? "There are no broker records in the system. Click Add Broker to create the first one."
                        : "No active brokers. Toggle Show inactive or add a new broker."
                    }
                  />
                ) : (
                  <WigAntTable
                    dataSource={rows}
                    columns={columns}
                    rowKey={(record) =>
                      record?.id != null ? String(record.id) : undefined
                    }
                    loading={loading}
                    showSearch={false}
                    showActionButton={false}
                    showExport
                    exportOptions={{ filename: "brokers" }}
                    showPagination
                    pagination={{
                      defaultPageSize: 100,
                      showSizeChanger: true,
                      pageSizeOptions: ["10", "25", "50", "100"],
                    }}
                    tableWidth="100%"
                    scroll={{ y: gridHeight }}
                    rowClassName={(record) =>
                      record?.id === selectedBrokerId ? "wig-active-row" : ""
                    }
                    onRow={(record) => ({
                      onClick: () => {
                        if (record?.id != null) setSelectedBrokerId(record.id);
                      },
                      style: { cursor: "pointer" },
                    })}
                  />
                )}

                {/* Child "Agents" panel (master/detail) — access-management
                    only. Lists the broker-access mappings for the selected
                    broker; supersedes the standalone Broker Access screen. */}
                {canManageAccess && (
                  <Card
                    size="small"
                    title={
                      selectedBroker
                        ? `Agents for ${selectedBroker.company_name} (${agentsForSelected.length})`
                        : "Agents"
                    }
                    extra={
                      <Button
                        type="primary"
                        icon={<UsergroupAddOutlined />}
                        onClick={openAddAgent}
                        disabled={!selectedBrokerId}
                      >
                        Add agent
                      </Button>
                    }
                  >
                    {accessError && (
                      <Alert
                        type="error"
                        showIcon
                        message="Failed to load agents"
                        description={accessError}
                        style={{ marginBottom: 8 }}
                      />
                    )}
                    {!selectedBrokerId ? (
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="Select a broker above to manage its agents."
                      />
                    ) : (
                      <WigAntTable
                        size="small"
                        rowKey={(record) => record?.email}
                        dataSource={agentsForSelected}
                        columns={agentColumns}
                        loading={accessLoading}
                        showSearch={false}
                        showExport={false}
                        showActionButton={false}
                        pagination={{
                          size: "small",
                          hideOnSinglePage: true,
                          pageSize: 10,
                        }}
                        scroll={{ x: "max-content", y: 240 }}
                        locale={{
                          emptyText: "No agents linked to this broker yet",
                        }}
                      />
                    )}
                  </Card>
                )}

                <BrokerFormModal
                  open={modalOpen}
                  mode={modalMode}
                  initialValues={modalInitialValues}
                  saving={saving}
                  onCancel={() => setModalOpen(false)}
                  onSubmit={modalMode === "add" ? handleCreate : handleUpdate}
                />

                {canManageAccess && (
                  <AddAgentModal
                    open={agentModalOpen}
                    mode={agentModalMode}
                    broker={selectedBroker}
                    initialAgent={editingAgent}
                    aoiOptions={aoiOptions}
                    onCancel={() => setAgentModalOpen(false)}
                    onSubmit={handleAddAgent}
                    onReassign={handleReassignAgent}
                    onUpdate={handleUpdateAgent}
                    onSuccess={onAgentModalSuccess}
                  />
                )}
              </Space>
            }
          />
        </Row>
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <SignIn />
      </UnauthenticatedTemplate>
    </React.Fragment>
  );
};

export default Brokers;
